//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useState } from 'react';
import {
  Box, CircularProgress, Collapse, Divider, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputIcon from '@mui/icons-material/Input';

import { drawerRowSx } from './drawerRowSx';
import { demoData } from '@/utils/DemoDatasets';

interface DemoDrawerSectionProps {
  // Keyed by dataset name, the shape useDemoDataLoader reports.
  isLoading: Record<string, boolean>;
  isLoaded: Record<string, boolean>;
  onLoad: (datasetName: string) => void;
}

/**
 * The mobile drawer's demo-data rows. On desktop the demo tool anchors a
 * Popper menu to the launcher that was clicked; a drawer row unmounts when
 * the drawer closes, so a menu anchored to one would point at a detached
 * node. The datasets render as drawer rows instead, in a collapsible
 * section shaped like the plugins section beside it.
 */
const DemoDrawerSection = ({
  isLoading, isLoaded, onLoad
}: DemoDrawerSectionProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const rowSx = drawerRowSx(theme);

  return (
    <>
      <Divider
        component="li"
        sx={{ borderColor: theme.palette.border.default, my: 1, mx: 2 }}
      />
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => setExpanded((prev) => !prev)}
          aria-label="Demo data"
          aria-expanded={expanded}
          sx={rowSx}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <InputIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase'
                }}
              >
                Demo data
              </Typography>
            }
          />
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
      </ListItem>
      <Collapse component="li" in={expanded} timeout="auto" unmountOnExit>
        <List disablePadding>
          {Object.entries(demoData).map(([datasetName, { label, icon }]) => (
            <ListItem key={datasetName} disablePadding>
              <ListItemButton
                onClick={() => onLoad(datasetName)}
                disabled={isLoading[datasetName] || isLoaded[datasetName]}
                aria-busy={isLoading[datasetName]}
                sx={{ ...rowSx, pl: 4 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {isLoading[datasetName] ? (
                    <Box sx={{ display: 'flex' }}>
                      <CircularProgress size={17} aria-hidden />
                    </Box>
                  ) : (
                    icon
                  )}
                </ListItemIcon>
                <ListItemText primary={`Load ${label} Data`} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default DemoDrawerSection;
